import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, X, Loader2 } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import MaterialItemSelector from '../MaterialItemSelector';
import EmployeeSelect from '../pickers/EmployeeSelect';

/**
 * CreateIssueForm — direct Material Issue to a work order / location, FIFO-
 * consumed against stock on hand. Used on desktop (Stores > Material Issues)
 * and mobile. Same picker + cap-enforcement as MR/Return (MaterialItemSelector
 * mode="issue" caps at min(stock on hand, PMS cap)).
 *
 * ADM-CR-02: locationType="office" issues from the Office location to an
 * active employee (no project — no MR concept for office supplies).
 */
export function CreateIssueForm({ projectId = '', onCreated, onCancel, compact = false, restrictToOffice = false }) {
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(projectId || '');
  const [issuedTo, setIssuedTo] = useState('');
  const [remarks, setRemarks] = useState('');
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [locationType, setLocationType] = useState(restrictToOffice ? 'office' : 'project');
  const [officeLocations, setOfficeLocations] = useState([]);
  const [officeLocationId, setOfficeLocationId] = useState('');
  const [issuedToEmployee, setIssuedToEmployee] = useState(null);

  useEffect(() => {
    apiGet('/projects').then(r => setProjects(Array.isArray(r) ? r : (r?.items || r?.projects || []))).catch(() => {});
    apiGet('/config-masters/office_locations').then((d) => {
      const vals = d?.values || [];
      setOfficeLocations(vals);
      if (vals.length === 1) setOfficeLocationId(vals[0].id);
    }).catch(() => {});
  }, []);
  useEffect(() => { if (projectId) setProject(projectId); }, [projectId]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (locationType === 'office') {
      if (!officeLocationId) return toast.error('Select an office location');
      if (!issuedToEmployee) return toast.error('Select an active employee to issue to');
    } else {
      if (!project) return toast.error('Select a project');
      if (!issuedTo.trim()) return toast.error('Enter who/where this is issued to');
    }
    if (!items.length) return toast.error('Add at least one material line');
    setSaving(true);
    try {
      const r = await apiPost('/stores/issue', {
        location_type: locationType,
        project_id: locationType === 'project' ? project : undefined,
        office_location_id: locationType === 'office' ? officeLocationId : undefined,
        issued_to: locationType === 'office' ? issuedToEmployee.name : issuedTo,
        issued_to_employee_id: locationType === 'office' ? issuedToEmployee.id : undefined,
        remarks: remarks || undefined,
        items: items.map(i => ({ boq_id: i.boq_id, issued_qty: i.quantity })),
      });
      toast.success(`${r.issue_no || 'Issue'} recorded — stock consumed FIFO`);
      setItems([]); setIssuedTo(''); setRemarks(''); setIssuedToEmployee(null);
      onCreated?.(r);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Could not record issue'));
    } finally { setSaving(false); }
  };

  const field = 'border border-slate-300 rounded-sm px-3 py-2 text-sm w-full focus:outline-none focus:border-[#3b4872] bg-white';
  return (
    <form onSubmit={submit} className={`space-y-3 ${compact ? '' : 'bg-white border border-slate-200 rounded-md p-4'}`} data-testid="create-issue-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-slate-800">New Material Issue</h3>
        {onCancel && <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-700" data-testid="create-issue-cancel"><X size={16} /></button>}
      </div>
      {!projectId && !restrictToOffice && (
        <div className="flex gap-4 text-xs">
          <label className="flex items-center gap-1.5">
            <input type="radio" checked={locationType === 'project'} onChange={() => { setLocationType('project'); setItems([]); }} data-testid="create-issue-loc-project" />
            Project / Site
          </label>
          <label className="flex items-center gap-1.5">
            <input type="radio" checked={locationType === 'office'} onChange={() => { setLocationType('office'); setItems([]); }} data-testid="create-issue-loc-office" />
            Office
          </label>
        </div>
      )}
      {locationType === 'office' ? (
        <div className={`grid gap-3 ${compact ? 'grid-cols-1' : 'grid-cols-2'}`}>
          <label className="text-xs text-slate-600">Office Location
            <select value={officeLocationId} onChange={e => setOfficeLocationId(e.target.value)} className={field} data-testid="create-issue-office-location">
              <option value="">Select office…</option>
              {officeLocations.map(o => <option key={o.id} value={o.id}>{o.name || o.id}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-600">Issued To (active employee) *
            <EmployeeSelect value={issuedToEmployee} onSelect={setIssuedToEmployee} placeholder="Search employee…" testid="create-issue-employee" />
          </label>
        </div>
      ) : (
        <div className={`grid gap-3 ${compact ? 'grid-cols-1' : 'grid-cols-2'}`}>
          <label className="text-xs text-slate-600">Project
            <select value={project} onChange={e => { setProject(e.target.value); setItems([]); }} className={field} disabled={!!projectId} data-testid="create-issue-project">
              <option value="">Select project…</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name || p.code}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-600">Issued to *
            <Input value={issuedTo} onChange={e => setIssuedTo(e.target.value)} placeholder="e.g. Work order WO-12 / Contractor name" data-testid="create-issue-issued-to" />
          </label>
        </div>
      )}
      <MaterialItemSelector
        projectId={locationType === 'project' ? project : ''}
        officeLocationId={locationType === 'office' ? officeLocationId : ''}
        mode="issue" selectedItems={items} onItemsChange={setItems}
      />
      <Input value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Remarks (optional)" data-testid="create-issue-remarks" />
      <div className="flex justify-end gap-2">
        {onCancel && <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>}
        <Button type="submit" disabled={saving} className="bg-[#3b4872] hover:bg-[#2d3856]" data-testid="create-issue-submit">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Record Issue
        </Button>
      </div>
    </form>
  );
}

export default CreateIssueForm;

